import {
  ExternalProjectImportError,
  ExternalProjectImportPreview,
} from '@provision/entity-types';
import { BackgroundJobItem } from 'src/graphql/entities/background-job/background-job.types';
import { useBackgroundJob } from 'src/graphql/entities/background-job/hooks/use-background-job';
import { useBackgroundJobItems } from 'src/graphql/entities/background-job/hooks/use-background-job-items';
import { parseJsonSafely } from 'src/utils/objects';

export interface DtoolsProjectImportJobParams {
  projectIds: string[];
  dryRun: boolean;
}

/**
 * Polls a D-Tools import (or preview) job and, once it finishes, turns its items into the
 * per-project records and errors the import views render.
 */
export const useDtoolsProjectImportJobOutcome = (jobId: string | null | undefined) => {
  const { job, isFinished, loading: jobLoading } = useBackgroundJob(jobId);
  const {
    items,
    hasMore,
    loadMore,
    loading: itemsLoading,
  } = useBackgroundJobItems({ jobId, skip: !isFinished });

  const params = parseJsonSafely<DtoolsProjectImportJobParams>(job?.params ?? '');

  return {
    job,
    isFinished,
    projectIds: params?.projectIds ?? [],
    isDryRun: params?.dryRun ?? false,
    records: toRecords(items),
    errors: toErrors(items),
    loading: jobLoading || (isFinished && itemsLoading && !items.length),
    hasMoreResults: hasMore,
    loadMoreResults: loadMore,
  };
};

export type DtoolsProjectImportJobOutcome = ReturnType<
  typeof useDtoolsProjectImportJobOutcome
>;

function toRecords(items: BackgroundJobItem[]): ExternalProjectImportPreview[] {
  return items
    .filter((item) => item.status === 'succeeded')
    .map((item) => parseJsonSafely<ExternalProjectImportPreview>(item.result ?? ''))
    .filter((record): record is ExternalProjectImportPreview => Boolean(record));
}

function toErrors(items: BackgroundJobItem[]): ExternalProjectImportError<string>[] {
  return items
    .filter((item) => item.status === 'failed')
    .map((item) => ({
      projectId: item.itemKey,
      error: item.error?.message ?? 'The project could not be imported',
    }));
}
